import { useTranslation } from 'react-i18next';
import {
  LegacyTimeCountdown,
  legacyFileUrl,
  legacyProductPaidLabelKey,
  type LegacyOrder,
} from '@/entities/legacy-order';

/**
 * The "Статус товара" column — the old grid's `product_paid` cell
 * (`tableHeaderSelector.tsx:313-411`): up to one debt countdown stacked above
 * the four-way payment label, with the courier's payment-receipt thumbnail
 * beside the label when one exists.
 *
 * The countdowns, both from the source:
 * - payment status 6 (courier owes for the goods) while the order hasn't
 *   finished (stage ≤ 15) and a pickup has been handed over: a live timer
 *   against `handed_over_at + 3h` — the courier's settlement window.
 * - payment status 7 (debt under review): a STATIC diff between when the
 *   goods changed hands and when the courier paid, green when the debt was
 *   settled within 5 hours, red past that.
 */
export function LegacyProductPaidCell({
  order,
  onShowReceipt,
}: {
  order: LegacyOrder;
  /** Opens the page-level lightbox with the receipt photo. */
  onShowReceipt: (paths: string[]) => void;
}) {
  const { t } = useTranslation('legacy');

  const handedOverAt =
    order.origin.find((leg) => leg.handed_over_at)?.handed_over_at ?? null;

  const labelKey = legacyProductPaidLabelKey({
    totalProductPrice: order.total_product_price,
    productPaid: order.delivery?.product_paid ?? false,
    courierPaymentStatus: order.courier_payment_status,
  });

  const receiptPath = order.courier_payment_receipt_url;
  const receiptSrc = legacyFileUrl(receiptPath);

  const showDebtCountdown =
    order.courier_payment_status === 6 && order.stage <= 15 && handedOverAt !== null;

  const showReviewTimer =
    order.courier_payment_status === 7 &&
    order.stage <= 15 &&
    handedOverAt !== null &&
    order.courier_paid_at !== null;

  return (
    <div className="flex flex-col gap-1">
      {showDebtCountdown && handedOverAt ? (
        <LegacyTimeCountdown onlyText endTime={addHours(handedOverAt, 3)} />
      ) : null}

      {showReviewTimer && handedOverAt && order.courier_paid_at ? (
        <LegacyTimeCountdown
          onlyShow
          endTime={order.courier_paid_at}
          doneAt={handedOverAt}
          color={reviewColor(handedOverAt, order.courier_paid_at)}
        />
      ) : null}

      <div className="flex items-center gap-1">
        <span className="whitespace-pre-line">{t(labelKey)}</span>

        {receiptPath && receiptSrc ? (
          <button
            type="button"
            aria-label={t('single.ofd')}
            className="cursor-zoom-in"
            onClick={(e) => {
              // Never let the zoom fall through to the row's open-drawer
              // click — the merchant asked to see the receipt, not the order.
              e.stopPropagation();
              onShowReceipt([receiptPath]);
            }}
          >
            <img
              src={receiptSrc}
              alt=""
              className="mt-1 max-h-[30px] w-[30px] rounded-[6px] object-contain"
            />
          </button>
        ) : null}
      </div>
    </div>
  );
}

/**
 * `handed_over_at + n hours`, falling back to "now" on an unparseable stamp —
 * the old cell's own `dayjs(...).isValid() ? ... : new Date()` fallback.
 */
function addHours(iso: string, hours: number): Date {
  const base = new Date(iso);
  if (Number.isNaN(base.getTime())) return new Date();
  return new Date(base.getTime() + hours * 3_600_000);
}

/**
 * Green when the courier settled within 5 hours (300 min) of receiving the
 * goods, red otherwise.
 */
function reviewColor(handedOverAt: string, paidAt: string): 'green' | 'red' {
  const diffMin = Math.floor(
    (new Date(paidAt).getTime() - new Date(handedOverAt).getTime()) / 60_000,
  );
  return diffMin <= 300 ? 'green' : 'red';
}
